import React, { useState, useEffect } from 'react';
import { API_BASE } from '../utils/api';

interface Facility {
  id: number;
  name: string;
  address: string;
}

interface Product {
  id?: number;
  facility_id: number;
  name: string;
  ingredients?: string;
  product_type?: string;
  intended_use?: string;
  target_audience?: string;
  safety_data_available?: boolean;
}

interface ProductFormProps {
  onSubmit: (product: Omit<Product, 'id' | 'created_at' | 'updated_at'>) => void;
  initialData?: Product;
}

const ProductForm: React.FC<ProductFormProps> = ({ onSubmit, initialData }) => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [formData, setFormData] = useState<Product>({
    facility_id: initialData?.facility_id || 0,
    name: initialData?.name || '',
    ingredients: initialData?.ingredients || '',
    product_type: initialData?.product_type || 'cosmetic',
    intended_use: initialData?.intended_use || '',
    target_audience: initialData?.target_audience || '',
    safety_data_available: initialData?.safety_data_available || false,
  });

  // Centralized API base

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`${API_BASE}/facilities/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setFacilities(data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'facility_id' ? parseInt(value) : value
    }));
  };

  const handleIngredientsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      ingredients: e.target.value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information Section */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
            <label htmlFor="facility_id" className="block text-sm font-semibold text-gray-700 mb-2">
          Facility *
        </label>
        <select
          id="facility_id"
          name="facility_id"
          value={formData.facility_id}
          onChange={handleChange}
          required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-gray-900"
        >
          <option value="">Select a facility</option>
          {facilities.map((facility) => (
            <option key={facility.id} value={facility.id}>
              {facility.name} - {facility.address}
            </option>
          ))}
        </select>
      </div>
      <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
          Product Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
              placeholder="Enter product name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-gray-900 placeholder-gray-500"
        />
      </div>
        </div>
      </div>

      {/* Product Details Section */}
      <div className="bg-blue-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Product Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
            <label htmlFor="product_type" className="block text-sm font-semibold text-gray-700 mb-2">
          Product Type
        </label>
        <select
          id="product_type"
          name="product_type"
          value={formData.product_type}
          onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900"
        >
          <option value="cosmetic">Cosmetic</option>
          <option value="personal_care">Personal Care</option>
          <option value="fragrance">Fragrance</option>
        </select>
      </div>
      <div>
            <label htmlFor="intended_use" className="block text-sm font-semibold text-gray-700 mb-2">
          Intended Use
        </label>
        <input
          type="text"
          id="intended_use"
          name="intended_use"
          value={formData.intended_use}
          onChange={handleChange}
          placeholder="e.g., Anti-aging treatment"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900 placeholder-gray-500"
            />
          </div>
        </div>
        <div className="mt-6">
          <label htmlFor="ingredients" className="block text-sm font-semibold text-gray-700 mb-2">
            Ingredients
          </label>
          <textarea
            id="ingredients"
            name="ingredients"
            value={formData.ingredients}
            onChange={handleIngredientsChange}
            placeholder="e.g., Retinol, Hyaluronic Acid, Vitamin C"
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900 placeholder-gray-500 resize-none"
          />
          <p className="mt-2 text-sm text-gray-600">List all ingredients separated by commas</p>
        </div>
      </div>

      {/* Target & Safety Section */}
      <div className="bg-green-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          Target & Safety Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
            <label htmlFor="target_audience" className="block text-sm font-semibold text-gray-700 mb-2">
          Target Audience
        </label>
        <input
          type="text"
          id="target_audience"
          name="target_audience"
          value={formData.target_audience}
          onChange={handleChange}
          placeholder="e.g., Adults 30+"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-gray-900 placeholder-gray-500"
        />
      </div>
      <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Safety Data Status
            </label>
            <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            name="safety_data_available"
            checked={formData.safety_data_available}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              safety_data_available: e.target.checked
            }))}
                className="h-5 w-5 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-1"
          />
              <div className="min-w-0 flex-1">
                <label htmlFor="safety_data_available" className="text-sm font-semibold text-gray-700">
                  Safety data available
        </label>
                <p className="text-sm text-gray-600 mt-1">Check if safety documentation is available for this product</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
      <button
        type="submit"
          className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-3 rounded-lg hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {initialData ? 'Update Product' : 'Create Product'}
      </button>
      </div>
    </form>
  );
};

export default ProductForm; 